import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { 
  ArrowUpIcon, 
  ArrowDownIcon, 
  RefreshCwIcon, 
  TrophyIcon, 
  HomeIcon, 
  BuildingIcon, 
  AlertTriangleIcon, 
  TrendingUpIcon,
  SearchIcon
} from "lucide-react";
import { MarketSimulatorDifficulty, DifficultyOption } from "./MarketSimulatorDifficulty";
import { MockCompany, mockCompanies, getRiskLevelDescription, getRewardDescription, getVolatilityDescription } from "./mockCompanies";
import { MarketSearch } from "./MarketSearch";

interface StockData {
  time: string;
  price: number;
}

interface Achievement {
  id: string;
  title: string;
  description: string;
  unlocked: boolean;
  icon: React.ReactNode;
}

export const MarketSimulator = () => {
  const { toast } = useToast();
  const [difficulty, setDifficulty] = useState<DifficultyOption | null>(null);
  const [balance, setBalance] = useState(0);
  const [stockPrice, setStockPrice] = useState(100);
  const [shares, setShares] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [priceHistory, setPriceHistory] = useState<StockData[]>([]);
  const [isSimulating, setIsSimulating] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState<MockCompany | null>(null);
  const [companyPrices, setCompanyPrices] = useState<Record<string, number>>(() => {
    const initialPrices: Record<string, number> = {};
    mockCompanies.forEach(company => {
      initialPrices[company.id] = 100;
    });
    console.log("Initialized company prices:", initialPrices);
    return initialPrices;
  });

  const [achievements, setAchievements] = useState<Achievement[]>([
    {
      id: 'first_trade',
      title: 'First Steps',
      description: 'Make your first trade',
      unlocked: false,
      icon: <TrophyIcon className="w-4 h-4 text-yellow-500" />
    },
    {
      id: 'profit_master',
      title: 'Profit Master',
      description: 'Achieve 50% profit on your initial investment',
      unlocked: false,
      icon: <TrophyIcon className="w-4 h-4 text-green-500" />
    },
    {
      id: 'day_trader',
      title: 'Day Trader',
      description: 'Complete 10 trades in one session',
      unlocked: false,
      icon: <TrophyIcon className="w-4 h-4 text-blue-500" />
    }
  ]);
  const [tradeCount, setTradeCount] = useState(0);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSector, setSelectedSector] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<"risk" | "reward" | "volatility" | null>(null);

  const filteredCompanies = useMemo(() => {
    let filtered = mockCompanies;

    if (searchTerm) {
      filtered = filtered.filter(company => 
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.symbol.toLowerCase().includes(searchTerm.toLowerCase()) ||
        company.sector.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedSector) {
      filtered = filtered.filter(company => company.sector === selectedSector);
    }

    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        switch (sortBy) {
          case "risk":
            return b.riskLevel - a.riskLevel;
          case "reward":
            return b.potentialReward - a.potentialReward;
          case "volatility":
            return b.volatility - a.volatility;
          default:
            return 0;
        }
      });
    }

    return filtered;
  }, [searchTerm, selectedSector, sortBy]);

  const sectors = useMemo(() => 
    Array.from(new Set(mockCompanies.map(company => company.sector))),
    []
  );

  const handleDifficultySelect = (selectedDifficulty: DifficultyOption) => {
    setDifficulty(selectedDifficulty);
    setBalance(selectedDifficulty.startingBalance);
    setStockPrice(100);
    setShares(0);
    setPriceHistory([]);
    setIsSimulating(true);
    setTradeCount(0);
    setAchievements(prev => prev.map(a => ({ ...a, unlocked: false })));
  };

  useEffect(() => {
    if (difficulty) {
      const initialPrices: Record<string, number> = {};
      mockCompanies.forEach(company => {
        initialPrices[company.id] = 100;
      });
      setCompanyPrices(initialPrices);
      if (!selectedCompany) {
        setSelectedCompany(mockCompanies[0]);
      }
      console.log("Difficulty selected, initialized prices:", initialPrices);
    }
  }, [difficulty]);

  useEffect(() => {
    if (!isSimulating || !difficulty || !selectedCompany) {
      console.log("Simulation paused or missing requirements");
      return;
    }

    const interval = setInterval(() => {
      setCompanyPrices(prev => {
        const newPrices = { ...prev };
        mockCompanies.forEach(company => {
          const volatilityFactor = difficulty.volatility * (company.volatility / 3);
          const change = (Math.random() - 0.5) * 5 * volatilityFactor;
          const currentPrice = prev[company.id] || 100;
          newPrices[company.id] = Math.max(1, currentPrice + change);
        });
        console.log("Updated prices:", newPrices);
        return newPrices;
      });

      if (selectedCompany) {
        const newPrice = companyPrices[selectedCompany.id] || 100;
        setStockPrice(Number(newPrice.toFixed(2)));
        setPriceHistory(prev => {
          const newHistory = [...prev, {
            time: new Date().toLocaleTimeString(),
            price: newPrice
          }];
          return newHistory.slice(-20);
        });
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [stockPrice, isSimulating, difficulty, selectedCompany]);

  useEffect(() => {
    if (!difficulty) return;

    const initialBalance = difficulty.startingBalance;
    const currentValue = balance + (shares * stockPrice);
    const profit = currentValue - initialBalance;
    const profitPercentage = (profit / initialBalance) * 100;

    setAchievements(prev => prev.map(achievement => {
      if (achievement.unlocked) return achievement;

      let shouldUnlock = false;

      switch (achievement.id) {
        case 'first_trade':
          shouldUnlock = tradeCount > 0;
          break;
        case 'profit_master':
          shouldUnlock = profitPercentage >= 50;
          break;
        case 'day_trader':
          shouldUnlock = tradeCount >= 10;
          break;
      }

      if (shouldUnlock && !achievement.unlocked) {
        toast({
          title: "Achievement Unlocked! 🏆",
          description: `${achievement.title}: ${achievement.description}`,
        });
      }

      return {
        ...achievement,
        unlocked: shouldUnlock
      };
    }));
  }, [tradeCount, balance, shares, stockPrice, difficulty]);

  const buyShares = () => {
    const cost = stockPrice * quantity;
    if (cost > balance) {
      toast({
        title: "Insufficient funds",
        description: "You don't have enough money to buy these shares.",
        variant: "destructive"
      });
      return;
    }

    setShares(shares + quantity);
    setBalance(Number((balance - cost).toFixed(2)));
    setTradeCount(prev => prev + 1);
    toast({
      title: "Shares purchased",
      description: `Successfully bought ${quantity} shares at $${stockPrice.toFixed(2)} each.`
    });
  };

  const sellShares = () => {
    if (quantity > shares) {
      toast({
        title: "Insufficient shares",
        description: "You don't have enough shares to sell.",
        variant: "destructive"
      });
      return;
    }

    const revenue = stockPrice * quantity;
    setShares(shares - quantity);
    setBalance(Number((balance + revenue).toFixed(2)));
    setTradeCount(prev => prev + 1);
    toast({
      title: "Shares sold",
      description: `Successfully sold ${quantity} shares at $${stockPrice.toFixed(2)} each.`
    });
  };

  const resetSimulation = () => {
    if (!difficulty) return;
    handleDifficultySelect(difficulty);
    toast({
      title: "Simulation reset",
      description: "Your balance and shares have been reset to initial values."
    });
  };

  const toggleSimulation = () => {
    setIsSimulating(!isSimulating);
    toast({
      title: isSimulating ? "Simulation paused" : "Simulation resumed",
      description: isSimulating ? "Price updates have been paused." : "Price updates have been resumed."
    });
  };

  if (!difficulty) {
    return <MarketSimulatorDifficulty onSelectDifficulty={handleDifficultySelect} />;
  }

  const portfolioValue = Number((shares * stockPrice).toFixed(2));
  const totalValue = Number((balance + portfolioValue).toFixed(2));
  const profitLoss = totalValue - difficulty.startingBalance;
  const profitLossPercentage = ((profitLoss / difficulty.startingBalance) * 100).toFixed(2);

  if (difficulty?.title === "Easy") {
    return (
      <div className="container mx-auto p-4 space-y-6">
        <Card className="bg-gradient-to-br from-primary/90 to-primary/80 border-primary/50 text-white">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <CardTitle className="flex items-center gap-2 text-white">
                  <span>Market Conquest - {difficulty.title}</span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={toggleSimulation}
                    className="ml-2 border-white/20 text-white hover:bg-white/20"
                  >
                    {isSimulating ? "Pause" : "Resume"}
                  </Button>
                </CardTitle>
                <div className="w-full max-w-md">
                  <div className="text-sm text-white/90 mb-1">Market Conquest Progress</div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <div 
                      className="bg-white h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(tradeCount / 10) * 100}%` }}
                    />
                  </div>
                  <div className="text-xs text-white/80 mt-1">
                    Dominate the market! Complete {10 - tradeCount} more trades to unlock expert status
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDifficulty(null)}
                className="text-white hover:text-white hover:bg-white/20"
              >
                <HomeIcon className="w-4 h-4 mr-2" />
                Change Difficulty
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="mb-6 space-y-4">
              <MarketSearch 
                companies={mockCompanies} 
                onSelectCompany={setSelectedCompany} 
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredCompanies.map((company) => (
                  <TooltipProvider key={company.id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Card 
                          key={company.id}
                          className={`
                            cursor-pointer transition-all duration-300 hover:scale-[1.02]
                            ${selectedCompany?.id === company.id 
                              ? 'bg-white/20 border-white' 
                              : 'bg-white/10 border-white/20'}
                          `}
                          onClick={() => setSelectedCompany(company)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <BuildingIcon className="w-4 h-4" />
                                <span className="font-bold">{company.symbol}</span>
                              </div>
                              <Badge variant={company.analystRating === "Strong Buy" ? "default" : "secondary"}>
                                {company.analystRating}
                              </Badge>
                            </div>
                            <div className="text-lg font-bold mb-1">
                              ${companyPrices[company.id]?.toFixed(2) || "0.00"}
                            </div>
                            <div className="text-sm opacity-80 mb-2">{company.name}</div>
                            
                            <div className="grid grid-cols-2 gap-2 mb-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1">
                                    <AlertTriangleIcon className="w-4 h-4" />
                                    <div>Risk: {"🔴".repeat(company.riskLevel)}</div>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{getRiskLevelDescription(company.riskLevel)}</p>
                                </TooltipContent>
                              </Tooltip>
                              
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1">
                                    <TrendingUpIcon className="w-4 h-4" />
                                    <div>Reward: {"⭐".repeat(company.potentialReward)}</div>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{getRewardDescription(company.potentialReward)}</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>

                            <div className="text-xs grid grid-cols-2 gap-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div>P/E: {company.peRatio}</div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Price to Earnings Ratio - Lower values may indicate better value</p>
                                </TooltipContent>
                              </Tooltip>
                              
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div>Yield: {company.dividendYield}%</div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Dividend Yield - Annual dividend payments as a percentage of stock price</p>
                                </TooltipContent>
                              </Tooltip>
                              
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div>Cap: {company.marketCap}</div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Market Capitalization - Total value of all shares</p>
                                </TooltipContent>
                              </Tooltip>
                              
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div>Vol: {"⚡".repeat(company.volatility)}</div>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>{getVolatilityDescription(company.volatility)}</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </CardContent>
                        </Card>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs">{company.description}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ))}
              </div>

              <div className="h-[300px] w-full bg-white/10 rounded-lg p-4">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={priceHistory}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#ffffff40" />
                    <XAxis dataKey="time" stroke="#ffffff90" />
                    <YAxis domain={['auto', 'auto']} stroke="#ffffff90" />
                    <RechartsTooltip 
                      contentStyle={{ 
                        backgroundColor: '#58CC02', 
                        border: '1px solid #ffffff40',
                        borderRadius: '8px',
                        color: '#ffffff'
                      }} 
                    />
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke="#ffffff" 
                      dot={false}
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div className="space-y-4">
                  {[
                    { label: 'Current Price', value: `$${stockPrice.toFixed(2)}` },
                    { label: 'Cash Balance', value: `$${balance.toFixed(2)}` },
                    { label: 'Shares Owned', value: shares },
                    { label: 'Portfolio Value', value: `$${portfolioValue}` },
                    { 
                      label: 'Profit/Loss', 
                      value: `$${profitLoss.toFixed(2)} (${profitLossPercentage}%)`,
                      isProfit: profitLoss >= 0
                    }
                  ].map((item, index) => (
                    <div 
                      key={item.label}
                      className={`
                        flex justify-between items-center p-4 rounded-lg
                        ${item.label === 'Profit/Loss' 
                          ? item.isProfit 
                            ? 'bg-green-500/20 text-green-300' 
                            : 'bg-red-500/20 text-red-300'
                          : 'bg-purple-950/50 text-purple-200'}
                      `}
                    >
                      <span className="font-semibold">{item.label}:</span>
                      <span className="text-lg">{item.value}</span>
                    </div>
                  ))}
                </div>

                <div className="space-y-4">
                  <div className="flex items-center space-x-2">
                    <Input
                      type="number"
                      min="1"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-32 bg-purple-950/50 border-purple-700 text-purple-200"
                    />
                    <Button 
                      onClick={buyShares}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <ArrowUpIcon className="w-4 h-4 mr-2" />
                      Buy
                    </Button>
                    <Button 
                      onClick={sellShares}
                      className="flex-1 bg-red-600 hover:bg-red-700"
                    >
                      <ArrowDownIcon className="w-4 h-4 mr-2" />
                      Sell
                    </Button>
                  </div>

                  <Button 
                    onClick={resetSimulation} 
                    variant="outline" 
                    className="w-full border-purple-600 text-purple-200 hover:bg-purple-800"
                  >
                    <RefreshCwIcon className="w-4 h-4 mr-2" />
                    Reset Simulation
                  </Button>

                  <Card className="bg-purple-950/50 border-purple-700">
                    <CardHeader>
                      <CardTitle className="text-lg text-purple-200">Achievements</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {achievements.map((achievement) => (
                          <div
                            key={achievement.id}
                            className={`
                              flex items-center gap-2 p-2 rounded
                              ${achievement.unlocked 
                                ? 'bg-yellow-500/20 text-yellow-300' 
                                : 'bg-purple-900/50 text-purple-400'}
                            `}
                          >
                            {achievement.icon}
                            <div>
                              <div className="font-medium">{achievement.title}</div>
                              <div className="text-sm opacity-80">{achievement.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-purple-950/50 border-purple-700">
                    <CardContent className="pt-6">
                      <h3 className="font-semibold mb-2 text-purple-200">Trading Tips:</h3>
                      <ul className="list-disc pl-4 space-y-1 text-sm text-purple-300">
                        <li>Watch for market trends before making decisions</li>
                        <li>Don't invest all your money at once</li>
                        <li>Use the pause button to analyze the market</li>
                        <li>Try to unlock all achievements!</li>
                      </ul>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 space-y-6">
      <Card className="bg-gradient-to-br from-primary/90 to-primary/80 border-primary/50 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <CardTitle className="flex items-center gap-2 text-white">
                <span>Market Conquest - {difficulty.title}</span>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={toggleSimulation}
                  className="ml-2 border-white/20 text-white hover:bg-white/20"
                >
                  {isSimulating ? "Pause" : "Resume"}
                </Button>
              </CardTitle>
              <div className="w-full max-w-md">
                <div className="text-sm text-white/90 mb-1">Market Conquest Progress</div>
                <div className="w-full bg-white/20 rounded-full h-2">
                  <div 
                    className="bg-white h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(tradeCount / 10) * 100}%` }}
                  />
                </div>
                <div className="text-xs text-white/80 mt-1">
                  Dominate the market! Complete {10 - tradeCount} more trades to unlock expert status
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDifficulty(null)}
              className="text-white hover:text-white hover:bg-white/20"
            >
              <HomeIcon className="w-4 h-4 mr-2" />
              Change Difficulty
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {mockCompanies.map((company) => (
              <TooltipProvider key={company.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Card 
                      key={company.id}
                      className={`
                        cursor-pointer transition-all duration-300 hover:scale-[1.02]
                        ${selectedCompany?.id === company.id 
                          ? 'bg-white/20 border-white' 
                          : 'bg-white/10 border-white/20'}
                      `}
                      onClick={() => setSelectedCompany(company)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <BuildingIcon className="w-4 h-4" />
                            <span className="font-bold">{company.symbol}</span>
                          </div>
                          <span className="text-sm opacity-80">{company.sector}</span>
                        </div>
                        <div className="text-lg font-bold mb-1">
                          ${companyPrices[company.id]?.toFixed(2) || "0.00"}
                        </div>
                        <div className="text-sm opacity-80 mb-2">{company.name}</div>
                        <div className="text-xs grid grid-cols-2 gap-2">
                          <div>P/E: {company.peRatio}</div>
                          <div>Yield: {company.dividendYield}%</div>
                          <div>Cap: {company.marketCap}</div>
                          <div>Vol: {"⚡".repeat(company.volatility)}</div>
                        </div>
                      </CardContent>
                    </Card>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">{company.description}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
